import { useMemo } from 'react';

import type { ZoneModel, ZoneTranslationsModel } from '@snowit/types/zones';

import { cleanDelKey, isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import omitter from '@snowit/utilities/omitter';

import useAuth from '../useAuth';
import useDistricts from '../useDistricts';
import useSchemas from '../useSchemas';
import useTenants from '../useTenants';

export default () => {
  const firebase = useFirebase();
  const { authType, claims } = useAuth();
  const { activeTenants: tenants, hasTenants } = useTenants();
  const { store, setStore } = useStore();
  const { districts, writeDistrict, getDistricts } = useDistricts();
  const {
    // createDocument,
    // editDocument,
    // writeSubDocument,
    writeDocument,
    writeSubDocument,
    deleteDocument,
  } = useSchemas();

  const syncDistrictZones = async (zoneUID: string | undefined, districtUID?: string | null) => {
    if (!zoneUID || isNewEntity(zoneUID)) return;
    for (const district of districts) {
      if (districtUID) {
        // Zone has district. check all districts and remove zone from old district and add it the new one
        if ((district.zoneUIDS ?? []).includes(zoneUID) && district.uid !== districtUID) {
          console.info('removing zone from district', district.uid, district.name);
          await writeDistrict(district.uid, {
            zoneUIDS: (district.zoneUIDS ?? []).filter((it) => it !== zoneUID),
          });
        } else if (!(district.zoneUIDS ?? []).includes(zoneUID) && district.uid === districtUID) {
          console.info('adding zone to district', district.uid, district.name);
          await writeDistrict(district.uid, {
            zoneUIDS: Array.from(new Set([...(district.zoneUIDS ?? []), zoneUID])),
          });
        }
      } else {
        // Zone has NO district. check all districts and remove the zone from their zoneUIDS if present
        if ((district.zoneUIDS ?? []).includes(zoneUID)) {
          console.info('removing zone from all districts', district.uid, district.name);
          await writeDistrict(district.uid, {
            zoneUIDS: (district.zoneUIDS ?? []).filter((it) => it !== zoneUID),
          });
        }
      }
    }
    await getDistricts();
  };

  const zones = useMemo((): ZoneModel[] => {
    return store.zones.list
      .filter((zone) => zone.name)
      .filter((zone) => {
        if (authType === 'ADMIN') return true;
        return (zone.tenants || []).some((_tenant) => (claims.tenants ?? []).includes(_tenant));
      })
      .filter((zone) => {
        if (!tenants.length) return true;
        return hasTenants(zone.tenants);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [claims, authType, store.zones.list, tenants]);

  const getZones = async (): Promise<ZoneModel[]> => {
    try {
      const res = await firebase.query<ZoneModel>({
        collectionName: 'zones',
        filters: (q) => q.limit(0),
      });
      setStore('zones', { list: res });
      return res;
    } catch (error) {
      console.error('zones_get', { error });
      return [];
    } finally {
      setStore('zones', { ready: true });
    }
  };

  const getZone = async (zoneUID: string): Promise<ZoneModel | undefined> => {
    try {
      const doc = await firebase.doc<ZoneModel>({ collectionName: 'zones', id: zoneUID });
      if (!doc) throw new Error('no document found');
      setStore('zones', {
        details: doc,
        list: store.zones.list.map((it) => (it.uid === doc.uid ? doc : it)),
      });
      return doc;
    } catch (error) {
      console.error({ error });
      return undefined;
    }
  };

  const getTranslations = async (zoneUID: string): Promise<ZoneTranslationsModel | undefined> => {
    try {
      const ref = await firebase.subcollectionDoc<ZoneTranslationsModel>({
        collectionName: 'zones',
        id: zoneUID,
        subcollectionName: 'details',
        subId: 'translations',
      });
      return ref;
    } catch (error) {
      console.error({ error });
      return undefined;
    }
  };

  const writeZone = async (
    zoneUID: string | undefined,
    data: Partial<ZoneModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        modelName: SchemaNames.ZONE,
        attributes: omitter(data),
        id: isNewEntity(zoneUID) ? undefined : zoneUID,
      });
      await syncDistrictZones(zoneUID, cleanDelKey(data.districtUID));
      return id;
    } catch (error: any) {
      console.error('writeZone', { error });
    }
  };

  const writeZoneTranslations = async (
    zoneUID: string,
    data: Partial<ZoneTranslationsModel>,
  ): Promise<string | undefined> => {
    try {
      if (isNewEntity(zoneUID)) return undefined;
      const id = await writeSubDocument({
        attributes: omitter(data),
        modelName: SchemaNames.ZONE_DETAILTRANSLATION,
        parentId: zoneUID,
        id: 'translations',
      });
      return id;
    } catch (error: any) {
      console.error('writeZoneTranslations', { error });
    }
  };

  const deleteZone = async (zoneUID: string): Promise<boolean> => {
    if (isNewEntity(zoneUID)) return false;
    return await deleteDocument({ modelName: SchemaNames.ZONE, id: zoneUID });
  };

  return {
    getZones,
    getZone,
    getTranslations,
    writeZone,
    writeZoneTranslations,
    deleteZone,
    zones,
    ready: store.zones.ready,
  };
};
